import React, { useEffect, useState } from "react";
import { Stack, Chip, Button, Link, Typography } from "@mui/material";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import SendIcon from "@mui/icons-material/Send";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import EditIcon from "@mui/icons-material/Edit";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PanelAccordion from "../PanelAccordion/panel-accordion";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../Router/router";
import { OpenLinkInNewTab, UserHasAcl } from "../../../Utils/helpers";
import { cacheService, fileService, orderService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { FileTransferInitiator } from "../../../Core/Api/Types/file-transfer-initiator";
import moment from "moment";
import { FileTransferStatus } from "../../../Core/Api/Types/file-transfer-status";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import TableCellName from "../../Table/TableCellName";
import ModalConfirmActionOpenButton from "../../Modals/ModalConfirmAction/modal-confirm-action-open-button";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { ModalContext } from "../../Modals";
import ModalManualTaskTemplate, {
  ModalManualTaskTemplateProps,
} from "../../Modals/ModalManualTaskTemplate/modal-manual-task-template";
import { DbOrderInfos } from "../../../Core/Api/Types/db-order-infos";
import ModalTransferEdit, { ModalTransferEditElementProps } from "../../Modals/ModalTransferEdit/modal-transfer-edit";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { useTranslation } from "react-i18next";
import ModalResendTransferEmail, {
  ModalResendTransferEmailElementProps,
} from "../../Modals/ModalResendTransferEmail/modal-resend-transfer-email";

export interface OrderItemTabDetailsProps {
  item: DbOrderItemInfos;
}

const OrderItemTabDetails: React.FC<OrderItemTabDetailsProps> = (props) => {
  const { t } = useTranslation();
  const { item } = props;

  const enqueueCustomSnackbar = useCustomSnackbar();
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const [order, setOrder] = useState<DbOrderInfos>();

  useEffect(() => {
    orderService.GetOrderById(item.orderId).then((response) => {
      if (response.success) setOrder(response.order!);
    });
  }, [item.orderId]);

  return (
    <>
      <PanelAccordion title={t("panel:accordionTitle.itemDetails")}>
        <Stack spacing={1} pt={1.5}>
          <MetadataListTextItem title={t("common:doability")}>
            <CheckCircleOutlinedIcon fontSize="small" color={props.item.doable ? "success" : "error"} />
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.majorDelivery")}>
            <CheckCircleOutlinedIcon fontSize="small" color={order?.isImportant ? "success" : "error"} />
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.sourceAssetIds")} color="primary.text" noWrapLongText={true}>
            {item?.product?.sourceAssetIds?.map((id: number, index) => (
              <Link
                component={RouterLink}
                to={`${routePaths.asset.path}/${id}`}
                target="_blank"
                underline="hover"
                variant="body1"
                ml={1}
                key={index}
              >
                {id}
              </Link>
            ))}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.destinationAssetIds")} color="primary.text" noWrapLongText={true}>
            {item?.product?.destinationAssetIds?.map((id: number, index) => (
              <Link
                component={RouterLink}
                to={`${routePaths.asset.path}/${id}`}
                target="_blank"
                underline="hover"
                variant="body1"
                ml={1}
                key={index}
              >
                {id}
              </Link>
            ))}
          </MetadataListTextItem>
          <MetadataListItem title={t("common:owner")}>
            <Chip label={item?.ownerUserName!} color="info" size="small" />
          </MetadataListItem>
          {item.editorName && (
            <MetadataListTextItem title={t("menu:title.modifiedBy")} color="text.placeholder">
              <Chip label={item.editorName} color="info" size="small" />
            </MetadataListTextItem>
          )}
          <MetadataListTextItem title={t("menu:title.costCenter")} color="text.placeholder">
            N/A
          </MetadataListTextItem>
          {item?.erpWorkOrderId === null ? (
            <MetadataListTextItem title={t("menu:title.erpWorkOrderId")} color="text.placeholder">
              {t("menu:item.noAssociatedWorkOrderInErp")}
            </MetadataListTextItem>
          ) : (
            <MetadataListTextItem title={t("menu:title.erpWorkOrderId")}>#{item?.erpWorkOrderId}</MetadataListTextItem>
          )}
          <MetadataListTextItem title={t("menu:title.personalNotes")} color="text.placeholder">
            N/A
          </MetadataListTextItem>
          {(item?.type === OrderProductType.TranscodeByProfile ||
            item?.type === OrderProductType.TranscodeBundleByProfile) &&
          item?.product?.settings?.externalBroadcasterId !== null ? (
            <MetadataListTextItem title={t("menu:title.broadcasterSpecificId")}>
              {item?.product?.settings?.externalBroadcasterId}
            </MetadataListTextItem>
          ) : (
            <MetadataListTextItem title={t("menu:title.broadcasterSpecificId")} color="text.placeholder">
              N/A
            </MetadataListTextItem>
          )}
          <MetadataListTextItem title={t("menu:title.deliveryMethod")} color="text.placeholder">
            {item?.product?.deliveryMethod !== null ? item?.product?.deliveryMethod : t("common:downloadLink")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:createdAt")} color="text.placeholder">
            {moment(item?.creationTime).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.endedAt")} color="text.placeholder">
            {item?.endTime
              ? moment(item?.endTime).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)
              : t("menu:item.notCompleted")}
          </MetadataListTextItem>
          {item?.watermarking && (
            <MetadataListTextItem title={t("menu:title.watermarkingEnabled")} color="text.placeholder">
              <CheckCircleOutlinedIcon fontSize="small" color={"success"} />
            </MetadataListTextItem>
          )}
        </Stack>
      </PanelAccordion>
      {item?.warningDetails && item?.warningDetails.length > 0 && (
        <PanelAccordion title={t("common:warnings")}>
          <Stack spacing={1} pt={1.5}>
            {item?.warningDetails?.map((warn) => (
              <MetadataListTextItem title={warn.type!.toString()} color="text.placeholder" align="right">
                {warn.message}
                <Link
                  component={RouterLink}
                  to={`${routePaths.asset.path}/${warn.objectId}`}
                  target="_blank"
                  underline="hover"
                  variant="body1"
                  ml={1}
                >
                  {warn.objectId}
                </Link>
              </MetadataListTextItem>
            ))}
          </Stack>
        </PanelAccordion>
      )}
      {item?.manualTasksTemplate && (
        <PanelAccordion title={t("panel:accordionTitle.manualTaskTemplate")}>
          <Stack spacing={1} pt={1.5}>
            <Button
              onClick={() => {
                openModal &&
                  openModal<ModalManualTaskTemplateProps>(ModalManualTaskTemplate, {
                    tpl: item?.manualTasksTemplate!,
                    closeModal: () => false,
                  });
              }}
              startIcon={<AssignmentIcon />}
            >
              {t("button:openWorkDetailsTemplate")}
            </Button>
          </Stack>
        </PanelAccordion>
      )}
      {item?.followerUserIds && item?.followerUserIds.length > 0 && (
        <PanelAccordion title={t("common:followers")}>
          <Stack spacing={1} pt={1.5}>
            {item?.followersName?.map((user, index) => (
              <TableCellName
                key={user}
                data={{
                  value: {
                    firstname: user,
                    lastname: "",
                    id: item?.followerUserIds![index],
                  },
                }}
              />
            ))}
          </Stack>{" "}
        </PanelAccordion>
      )}
      <PanelAccordion title={t("panel:accordionTitle.billingInfos")}>
        <Stack spacing={1} pt={1.5}>
          <MetadataListTextItem title={t("menu:title.externalContractDeailId")} color="text.placeholder">
            {item?.contractExternalDealId !== null ? item?.contractExternalDealId : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:customerName")} color="text.placeholder">
            {item?.customer !== null ? item?.customerName : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.customerId")} color="text.placeholder">
            {item?.customer !== null ? item?.customer.customerCodeId : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.customerAddress")} color="text.placeholder" align="right">
            {item?.customer !== null
              ? item.customer.address +
                ", " +
                item.customer.zipCode +
                ", " +
                item.customer.city +
                ", " +
                item.customer.country
              : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:price")} color="text.placeholder">
            {item?.price !== null ? item?.price?.price + " " + item?.price?.currency : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:contacts")} color="text.placeholder">
            {item.billingEmails !== null && item.billingEmails.length > 0
              ? item.billingEmails.map((email) => <Chip label={email} />)
              : t("menu:item.noAttachedContacts")}
          </MetadataListTextItem>
          {(UserHasAcl(AclTypes.aclSuperUser) || UserHasAcl(AclTypes.aclorderSendAckBillingManually)) && (
            <MetadataListItem title={t("menu:title.sendInvoiceAck")}>
              <Button
                startIcon={<SendIcon />}
                size="small"
                variant="outlined"
                onClick={() => {
                  orderService.SendInvoiceAck(item.id).then(() => {
                    enqueueCustomSnackbar(t("alert:success.billingAcknowledgeSent"), "success");
                  });
                }}
              >
                {t("common:send")}
              </Button>
            </MetadataListItem>
          )}
        </Stack>
      </PanelAccordion>
      {item?.billingAck !== null && (
        <PanelAccordion title={t("panel:accordionTitle.billingAcknowledgement")}>
          <Stack spacing={1} pt={1.5}>
            <MetadataListItem title={t("common:agent")}>{item.billingAck.apiType}</MetadataListItem>
            <MetadataListItem title={t("common:date")}>
              {item.billingAck.date ? moment(item.billingAck.date).format("DD/MM/YY HH:mm:ss") : t("common:unknown")}
            </MetadataListItem>
            <MetadataListItem title={t("common:success")}>
              {item.billingAck.success ? (
                <CheckCircleOutlinedIcon fontSize="small" color="success" />
              ) : (
                <CancelOutlinedIcon fontSize="small" color="error" />
              )}
            </MetadataListItem>
            <MetadataListItem title={t("common:message")}>{item.billingAck.message}</MetadataListItem>
          </Stack>
        </PanelAccordion>
      )}
      <PanelAccordion title={t("panel:accordionTitle.transferInfos")}>
        <Stack spacing={1} pt={1.5}>
          <MetadataListTextItem title="Type" color="text.placeholder">
            {item?.fileTransfer !== null
              ? item?.fileTransfer.initiator === FileTransferInitiator.AsperaConnectFasp
                ? "Aspera"
                : "HTTP"
              : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:expireDate")} color="text.placeholder">
            {item?.fileTransfer !== null
              ? item?.fileTransfer.expireDate !== null
                ? moment(item?.fileTransfer.expireDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)
                : t("menu:item.neverExpire")
              : t("common:notDefined")}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:status")} color="text.placeholder">
            {item?.fileTransfer !== null ? (
              <CheckCircleOutlinedIcon
                fontSize="small"
                color={item?.fileTransfer?.status === FileTransferStatus.Online ? "success" : "error"}
              />
            ) : (
              t("common:notDefined")
            )}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("common:downloadLink")} color="text.placeholder">
            {item?.fileTransfer !== null ? (
              <Link
                component={RouterLink}
                to={`${routePaths.download.path}/${item?.fileTransfer.key}`}
                target="_blank"
                underline="hover"
                variant="body1"
              >
                {t("menu:item.openDownloadPageLink")}
              </Link>
            ) : (
              t("common:notDefined")
            )}
          </MetadataListTextItem>
          {UserHasAcl(AclTypes.aclOrderResendTransferEmail) && item.fileTransfer && item.fileTransfer.emails && (
            <MetadataListItem title={t("menu:title.resendTransferEmail")}>
              <Button
                startIcon={<SendIcon />}
                size="small"
                variant="outlined"
                onClick={() => {
                  if (!openModal) return;

                  openModal<ModalResendTransferEmailElementProps>(ModalResendTransferEmail, {
                    itemId: item.id,
                    fileTransferEmails: item.fileTransfer?.emails || [],
                  });
                }}
              >
                {t("common:resend")}
              </Button>
            </MetadataListItem>
          )}
          {UserHasAcl(AclTypes.aclTransferExpire) &&
            item!.fileTransfer &&
            (!item!.fileTransfer!.expireDate || moment(item!.fileTransfer!.expireDate) > moment()) && (
              <MetadataListItem title={t("menu:title.removeDownloadLinkAccess")}>
                <ModalConfirmActionOpenButton
                  startIcon={<StopCircleIcon />}
                  modalMessage={t("modal:title.confirmDownloadLinkAccessRemove")}
                  displayComment={false}
                  onConfirm={() => {
                    fileService.ExpireTransfer(item!.fileTransfer!.id).then((response) => {
                      enqueueCustomSnackbar(t("alert:success.accessRemoved"), "success");
                    });
                  }}
                  size="small"
                  sx={{ p: 0.5 }}
                  variant="text"
                >
                  {t("button:expireLinkNow")}
                </ModalConfirmActionOpenButton>
              </MetadataListItem>
            )}
          {UserHasAcl(AclTypes.aclTransferUpdate) &&
            item!.fileTransfer &&
            (!item!.fileTransfer!.expireDate || moment(item!.fileTransfer!.expireDate) > moment()) && (
              <MetadataListItem title={t("menu:title.updateExpirationDateOrRecipients")}>
                <Button
                  startIcon={<EditIcon />}
                  size="small"
                  sx={{ p: 0.5 }}
                  variant="text"
                  onClick={() => {
                    if (!openModal) return;
                    openModal<ModalTransferEditElementProps>(ModalTransferEdit, {
                      transfer: item!.fileTransfer!,
                      onConfirm: () => {},
                    });
                  }}
                >
                  {t("common:update")}
                </Button>
              </MetadataListItem>
            )}
        </Stack>
      </PanelAccordion>
      <PanelAccordion title={t("panel:accordionTitle.deliveryNotification")}>
        <Stack spacing={1} pt={1.5}>
          {item.fileTransfer?.emails && (
            <Stack direction={"row"} justifyContent={"space-between"}>
              <Typography color="text.secondary">{t("common:email_other")}</Typography>
              <Stack spacing={0.5} justifyContent={"flex-end"} direction={"row"} flexWrap={"wrap"} display={"flex"}>
                {item.fileTransfer.emails.map((x) => (
                  <Chip label={x} />
                ))}
              </Stack>
            </Stack>
          )}
          <MetadataListTextItem title={t("common:information")}>{t("menu:item.displayEmail")}</MetadataListTextItem>
          <MetadataListItem title={t("common:value")}>
            <Button
              startIcon={<VisibilityOutlinedIcon />}
              size="small"
              variant="outlined"
              disabled={item.status !== OrderStatus.COMPLETED}
              onClick={() =>
                OpenLinkInNewTab(
                  orderService.GetServiceUrl() + "/orders/items/" + item?.id + "/email/" + cacheService.GetAuthKey()
                )
              }
            >
              {t("common:show")}
            </Button>
          </MetadataListItem>
        </Stack>
      </PanelAccordion>
    </>
  );
};

export default OrderItemTabDetails;
